// src/pages/
import React, { useEffect, useState } from "react";
import { Upload } from "antd";
import {
  Plus,
  Upload as UploadIcon,
  Trash2,
  PlayCircle,
  Image as ImageIcon,
  Calendar,
  X,
  Check,
} from "lucide-react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import BottomTabBar from "../components/BottomTabBar";
import StoreSwitcher from "../components/StoreSwitcher";
import API_BASE from "../config/api";
import {
  buildSavePromoScheduleFromDraft,
  getPromoScheduleEntry,
  normalizeScheduleDays,
  savePromoSchedule,
  SCHEDULE_DAYS,
} from "../utils/promoScheduleApi";
import {
  validatePromoUploadFile,
  isVideoFile,
  MAX_VIDEO_UPLOAD_MB,
} from "../config/uploadLimits";
import {
  getUploadErrorMessage,
  updatePromoWithVideo,
} from "../utils/promoUploadApi";
import { useAppNotify } from '../utils/errorHandler';


export default function PromoAdsPage() {
  const { modeId } = useParams();
  const navigate = useNavigate();

  const { showNotify } = useAppNotify();


  const [promos, setPromos] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [commonTitle, setCommonTitle] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // EDIT MODAL
  const [editVisible, setEditVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editImage, setEditImage] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPreview, setEditPreview] = useState(null);

  const [scheduleVisible, setScheduleVisible] = useState(false);
  const [scheduleRecord, setScheduleRecord] = useState(null);
  const [draftTime, setDraftTime] = useState("all");
  const [draftDays, setDraftDays] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("customer_id")) {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    if (!modeId) {
      showNotify("Screen mode not found. Please go back and try again.", "error");
      navigate("/modes");
      return;
    }
    fetchPromos();
  }, [modeId]);

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/fetchPromosByMode/${modeId}`);
      setPromos(data.success ? data.data : []);
    } catch (err) {
      console.error(err);
      showNotify("Couldn't load promo images. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ------------------- IMAGE PROCESSING -------------------
  // Resize to 1920x1080 and rotate if portrait
  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;
        let rotate = false;

        if (height > width) {
          rotate = true;
          [width, height] = [height, width];
        }

        canvas.width = 1920;
        canvas.height = 1080;

        if (rotate) {
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((90 * Math.PI) / 180);
          ctx.drawImage(img, -height / 2, -width / 2, height, width);
          ctx.restore();
        } else {
          const scale = Math.min(1920 / width, 1080 / height);
          const newWidth = width * scale;
          const newHeight = height * scale;
          const dx = (1920 - newWidth) / 2;
          const dy = (1080 - newHeight) / 2;
          ctx.drawImage(img, dx, dy, newWidth, newHeight);
        }

        canvas.toBlob(
          (blob) => {
            const newFile = new File([blob], file.name, { type: file.type });
            newFile.uid = file.uid;
            resolve(newFile);
          },
          file.type,
          0.95
        );
      };

      img.onerror = () => {
        showNotify(`Failed to process image ${file.name}`, "error");
        reject();
      };
    });
  };

  // ------------------- BULK UPLOAD -------------------
  const beforeUpload = async (file) => {
    const isVideo = file.type?.startsWith("video/");

    if (isVideo) {
      const MAX_VIDEO_BYTES = MAX_VIDEO_UPLOAD_MB * 1024 * 1024;
      if (file.size > MAX_VIDEO_BYTES) {
        showNotify(`${file.name} exceeds ${MAX_VIDEO_UPLOAD_MB}MB video size limit.`, "error");
        return Upload.LIST_IGNORE;
      }
      setFileList((prev) => [...prev, file]);
      return false;
    }

    // existing image validation and processing
    const validation = validatePromoUploadFile(file);
    if (!validation.ok) {
      showNotify(validation.message, "error");
      return Upload.LIST_IGNORE;
    }

    const processed = await processImage(file);
    setFileList((prev) => [...prev, processed]);
    return false;
  };

  const handleRemoveLocal = (file) => {
    setFileList((prev) => prev.filter((f) => f.uid !== (file.uid || file.name)));
  };

  const handleBulkUpload = async () => {
    if (!fileList.length) {
      showNotify("Select files first", "warning");
      return;
    }

    const customerId = localStorage.getItem("customer_id");
    if (!customerId) {
      showNotify("Please log in again", "error");
      return;
    }

    try {
      setUploading(true);
      const title = commonTitle.trim() === "" ? "None" : commonTitle.trim();

      const formData = new FormData();
      fileList.forEach((file) => formData.append("file", file));
      formData.append("modeId", modeId);
      formData.append("customerId", customerId);
      formData.append("title", title);
      await axios.post(`${API_BASE}/addPromoAds`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showNotify("Uploaded successfully", "success");
      setFileList([]);
      setCommonTitle("");
      fetchPromos();
    } catch (err) {
      console.error(err);
      showNotify(getUploadErrorMessage(err), "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.post(`${API_BASE}/deletePromo`, { _id: id });
      if (res.data.success) {
        showNotify("Deleted", "success");
        fetchPromos();
      } else {
        showNotify(res.data.message || "Delete failed", "error");
      }
    } catch (err) {
      console.error(err);
      showNotify("Couldn't delete this promo. Please try again.", "error");
    }
  };

  // ------------------- EDIT -------------------
  const openEditModal = (record) => {
    setEditData(record);
    setEditTitle(record.title || "");
    setEditImage(null);
    setEditPreview(record.S3link || null);
    setEditVisible(true);
  };

  const openScheduleModal = (record) => {
    setScheduleRecord(record);
    const scheduleEntry = getPromoScheduleEntry(record);
    const timeWindow = scheduleEntry?.timeWindow?.[0];

    if (scheduleEntry?.allTime) {
      setDraftTime("all");
    } else if (timeWindow?.start === "06:00" && timeWindow?.end === "12:00") {
      setDraftTime("morning");
    } else if (timeWindow?.start === "12:00" && timeWindow?.end === "16:00") {
      setDraftTime("afternoon");
    } else {
      setDraftTime("all");
    }

    setDraftDays(normalizeScheduleDays(scheduleEntry?.days));
    setScheduleVisible(true);
  };

  // Process image before adding to edit modal
  const handleEditImageUpload = async ({ file }) => {
    const validation = validatePromoUploadFile(file);
    if (!validation.ok) {
      showNotify(validation.message, "error");
      return false;
    }

    if (file.type && file.type.startsWith("video/")) {
      setEditImage(file);
      setEditPreview(URL.createObjectURL(file));
      return false;
    }
    const processed = await processImage(file);
    setEditImage(processed);

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setEditPreview(e.target.result);
    reader.readAsDataURL(processed);

    return false;
  };

  const handleUpdatePromo = async () => {
    if (!editData) return;

    const customerId = localStorage.getItem("customer_id");
    const title = editTitle.trim() || "None";

    try {
      if (editImage) {
        if (isVideoFile(editImage)) {
          await updatePromoWithVideo({
            file: editImage,
            customerId,
            promoId: editData._id,
            title,
            apiBase: API_BASE,
          });
        } else {
          const formData = new FormData();
          formData.append("_id", editData._id);
          formData.append("title", title);
          formData.append("file", editImage);

          const res = await axios.post(`${API_BASE}/editPromo`, formData, {
            timeout: 120000,
          });

          if (!res.data.success) {
            showNotify(res.data.message || "Update failed", "error");
            return;
          }
        }
      } else {
        const formData = new FormData();
        formData.append("_id", editData._id);
        formData.append("title", title);

        const res = await axios.post(`${API_BASE}/editPromo`, formData, {
          timeout: 120000,
        });

        if (!res.data.success) {
          showNotify(res.data.message || "Update failed", "error");
          return;
        }
      }

      showNotify("Updated Successfully", "success");
      setEditVisible(false);
      fetchPromos();
    } catch (err) {
      console.error(err);
      showNotify(getUploadErrorMessage(err), "error");
    }
  };

  const saveSchedule = async () => {
    if (!scheduleRecord) return;
    try {
      const data = await savePromoSchedule(
        buildSavePromoScheduleFromDraft(
          draftTime,
          draftDays,
          scheduleRecord._id,
        ),
      );
      if (data.success) {
        showNotify("Schedule saved", "success");
        setScheduleVisible(false);
        fetchPromos();
      } else {
        showNotify(data.message || "Failed to save schedule", "error");
      }
    } catch (err) {
      console.error(err);
      const detail =
        err.response?.data?.message ||
        err.response?.statusText ||
        err.message;
      showNotify(detail || "Error saving schedule", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col w-full min-w-0">
      <div className="bg-white px-3 sm:px-4 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-gray-100 shadow-sm flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 border border-gray-200 px-2.5 sm:px-3 py-1.5 rounded-full flex-shrink-0"
        >
          ← Back
        </button>
        <h1 className="text-lg sm:text-xl font-extrabold text-gray-900 text-center truncate px-2">
          Promo Ads
        </h1>
        <div className="w-[4.5rem] sm:w-16 flex-shrink-0" aria-hidden="true" />
      </div>

      <StoreSwitcher />

      <div className="px-3 sm:px-4 pt-4 flex-1 pb-24 sm:pb-28 max-w-2xl w-full mx-auto">
        <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100 mb-4">
          <input
            className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 mb-3"
            placeholder="Enter common title (optional)"
            value={commonTitle}
            onChange={(e) => setCommonTitle(e.target.value)}
          />

          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <Upload
              multiple
              beforeUpload={beforeUpload}
              fileList={fileList.map((f) => ({
                uid: f.uid || f.name,
                name: f.name,
              }))}
              onRemove={handleRemoveLocal}
              accept="image/*,video/*"
              showUploadList={false}
              className="w-full sm:flex-1 block"
            >
              <button
                type="button"
                className="w-full flex items-center justify-center gap-1.5 border border-gray-200 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-full bg-white"
              >
                <Plus size={14} /> Select content
              </button>
            </Upload>

            <button
              type="button"
              onClick={handleBulkUpload}
              disabled={!fileList.length || uploading}
              className="w-full sm:flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white text-sm font-bold px-4 py-2.5 rounded-full disabled:opacity-40"
            >
                <UploadIcon size={14} />
                {uploading
                  ? "Uploading..."
                  : fileList.length
                  ? `Upload (${fileList.length})`
                  : "Upload"}
            </button>
          </div>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-2 text-center leading-snug">
            Images up to 100 MB Â· Videos up to 100 MB
          </p>

          {fileList.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {fileList.map((f) => (
                  <div
                    key={f.uid || f.name}
                    className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2"
                  >
                    <span className="text-xs text-gray-600 truncate flex-1">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveLocal(f)}
                      className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
          )}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm sm:text-base font-bold text-gray-900">
            Promo items
          </span>
          <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {promos.length}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center mt-12 sm:mt-20">
              <div className="bg-white rounded-2xl px-6 py-4 shadow-sm flex items-center gap-3 border border-gray-100">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium text-gray-700">
                  Loading promos...
                </span>
              </div>
            </div>
        ) : promos.length === 0 ? (
          <div className="mt-12 sm:mt-20 flex flex-col items-center text-gray-400 px-4 text-center">
              <ImageIcon size={36} className="mb-3 opacity-30" />
              <p className="text-sm">No promo ads yet.</p>
            </div>
        ) : (
          <div className="space-y-3">
            {promos.map((record) => (
              <div
                key={record._id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 flex overflow-hidden min-w-0"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                    {record.S3link ? (
                      record.file_type === "video" ||
                      record.S3link.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                        <div className="relative w-full h-full">
                          <video
                            src={record.S3link}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <PlayCircle size={22} className="text-white" />
                          </div>
                        </div>
                      ) : (
                        <img
                          src={record.S3link}
                          alt={record.title}
                          className="w-full h-full object-cover"
                        />
                      )
                    ) : (
                      <ImageIcon size={22} className="text-gray-300" />
                    )}
                  </div>

                <div className="flex-1 px-2.5 sm:px-3 py-2 sm:py-2.5 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                      {record.title || "Untitled"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 mb-2">
                      {record.file_type === "video" ||
                      record.S3link?.match(/\.(mp4|webm|ogg|mov)$/i)
                        ? "Video"
                        : "Image"}
                    </p>
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => openEditModal(record)}
                      className="bg-blue-50 text-blue-700 text-xs font-semibold px-2 sm:px-2.5 py-1 rounded-lg"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setViewData(record);
                        setViewVisible(true);
                      }}
                      className="bg-green-50 text-green-700 text-xs font-semibold px-2 sm:px-2.5 py-1 rounded-lg"
                    >
                      View
                    </button>
                    {/* <button
                      type="button"
                      onClick={() => openScheduleModal(record)}
                      className="bg-orange-50 text-orange-700 text-xs font-semibold px-2 sm:px-2.5 py-1 rounded-lg"
                    >
                      Schedule
                    </button> */}
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(record)}
                      className="bg-red-50 p-1.5 rounded-lg"
                      aria-label="Delete promo"
                    >
                      <Trash2 size={13} className="text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomTabBar />

      {viewVisible && viewData && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white w-full max-w-xs sm:max-w-sm rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900 truncate pr-2">
                {viewData.title || "Promo"}
              </h2>
              <button
                type="button"
                onClick={() => setViewVisible(false)}
                className="p-0.5 flex-shrink-0"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="p-3">
              {viewData.file_type === "video" ||
              viewData.S3link?.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                <video
                  src={viewData.S3link}
                  controls
                  autoPlay
                  className="w-full max-h-48 rounded-lg object-cover"
                />
              ) : (
                <img
                  src={viewData.S3link}
                  alt={viewData.title}
                  className="w-full max-h-48 rounded-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {editVisible && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white w-full max-w-xs sm:max-w-sm rounded-2xl px-3.5 py-3 shadow-2xl">
            <div className="flex justify-between items-center mb-2.5">
              <h2 className="text-base font-bold text-gray-900">Edit promo</h2>
              <button
                type="button"
                onClick={() => setEditVisible(false)}
                className="p-0.5"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-2.5">
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Title
                </label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>

              {editPreview && (
                <div>
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    Content preview
                  </label>
                  {editImage?.type?.startsWith("video/") ||
                  editData?.file_type === "video" ||
                  editPreview?.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                    <video
                      src={editPreview}
                      controls
                      className="w-full h-28 rounded-lg object-cover"
                    />
                  ) : (
                    <img
                      src={editPreview}
                      alt=""
                      className="w-full h-28 rounded-lg object-cover"
                    />
                  )}
                </div>
              )}

              <Upload
                beforeUpload={() => false}
                onChange={handleEditImageUpload}
                maxCount={1}
                accept="image/*,video/*"
                showUploadList={false}
              >
                <div className="border-2 border-dashed border-gray-300 rounded-xl py-2.5 px-3 flex items-center justify-center gap-1.5 cursor-pointer">
                  <UploadIcon size={14} className="text-blue-600" />
                  <span className="text-xs font-semibold text-blue-600">
                    Upload new content
                  </span>
                </div>
              </Upload>
            </div>

            <button
              type="button"
              onClick={handleUpdatePromo}
              className="w-full bg-blue-600 text-white font-bold text-sm py-2.5 rounded-full mt-3 flex items-center justify-center gap-1.5"
            >
              <Check size={14} /> Update promo
            </button>
          </div>
        </div>
      )}

      {scheduleVisible && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white w-full max-w-xs sm:max-w-sm rounded-2xl px-3.5 py-3 shadow-2xl">
            <div className="flex justify-between items-center mb-2.5">
              <h2 className="text-base font-bold text-gray-900">Set schedule</h2>
              <button
                type="button"
                onClick={() => setScheduleVisible(false)}
                className="p-0.5"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <p className="text-xs font-bold text-gray-900 mb-1.5">Time window</p>
            <div className="space-y-1 mb-2.5">
              {[
                { value: "all", label: "All time" },
                { value: "morning", label: "6am â€“ 12pm" },
                { value: "afternoon", label: "12pm â€“ 4pm" },
              ].map((opt) => (
                <div
                  key={opt.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDraftTime(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setDraftTime(opt.value);
                  }}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer ${
                    draftTime === opt.value
                      ? "border-blue-300 bg-blue-50"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div
                    className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      draftTime === opt.value
                        ? "border-blue-600"
                        : "border-gray-300"
                    }`}
                  >
                    {draftTime === opt.value && (
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                    )}
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      draftTime === opt.value
                        ? "text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    {opt.label}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-xs font-bold text-gray-900 mb-0.5">Days</p>
            <p className="text-[10px] text-gray-400 mb-1.5 leading-tight">
              Pick days or use all days.
            </p>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setDraftDays([])}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setDraftDays([]);
              }}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer mb-1.5 ${
                draftDays.length === 0
                  ? "border-blue-300 bg-blue-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div
                className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  draftDays.length === 0
                    ? "border-blue-600 bg-blue-600"
                    : "border-gray-300 bg-white"
                }`}
              >
                {draftDays.length === 0 && (
                  <Check size={9} className="text-white" />
                )}
              </div>
              <span
                className={`text-xs font-semibold ${
                  draftDays.length === 0
                    ? "text-blue-700"
                    : "text-gray-700"
                }`}
              >
                All days
              </span>
            </div>

            <div className="grid grid-cols-3 gap-1 mb-2.5">
              {SCHEDULE_DAYS.map((day) => {
                const selected = draftDays.includes(day.value);
                return (
                  <div
                    key={day.value}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      setDraftDays((prev) =>
                        prev.includes(day.value)
                          ? prev.filter((d) => d !== day.value)
                          : [...prev, day.value]
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setDraftDays((prev) =>
                          prev.includes(day.value)
                            ? prev.filter((d) => d !== day.value)
                            : [...prev, day.value]
                        );
                      }
                    }}
                    className={`flex items-center justify-center gap-1 px-1 py-1.5 rounded-lg border cursor-pointer ${
                      selected
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`w-3 h-3 rounded border flex items-center justify-center flex-shrink-0 ${
                        selected
                          ? "border-blue-600 bg-blue-600"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {selected && (
                        <Check size={8} className="text-white" />
                      )}
                    </div>
                    <span
                      className={`text-[10px] font-medium ${
                        selected ? "text-blue-700" : "text-gray-600"
                      }`}
                    >
                      {day.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={saveSchedule}
              className="w-full bg-blue-600 text-white font-bold text-sm py-2.5 rounded-full flex items-center justify-center gap-1.5"
            >
              <Calendar size={14} /> Save schedule
            </button>
          </div>
        </div>
      )}


      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-sm shadow-2xl">
            <div className="bg-red-50 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete promo</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete{" "}
              <strong className="text-gray-900">
                &quot;{deleteConfirm.title || "this promo"}&quot;
              </strong>
              ? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDelete(deleteConfirm._id);
                  setDeleteConfirm(null);
                }}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
